import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';

import { APP_LANG_STORAGE_KEY } from './app-lang.constants';
import { preloadAppLang } from './preload-lang-scopes';

const STORAGE_KEY = APP_LANG_STORAGE_KEY;
const AVAILABLE_LANGS = ['pt-BR', 'en-US', 'es-ES'] as const;
type AppLang = (typeof AVAILABLE_LANGS)[number];

function normalizeStoredLang(value: string | null): AppLang | null {
    if (!value) return null;
    return AVAILABLE_LANGS.includes(value as AppLang) ? (value as AppLang) : null;
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
    private langSwitchRequest = 0;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private transloco: TranslocoService,
    ) {}

    init(): void {
        if (!isPlatformBrowser(this.platformId)) return;

        const stored = normalizeStoredLang(localStorage.getItem(STORAGE_KEY));
        const lang = stored ?? 'pt-BR';

        localStorage.setItem(STORAGE_KEY, lang);
        this.transloco.setActiveLang(lang);
    }

    set(lang: string): void {
        const normalized = normalizeStoredLang(lang) ?? 'pt-BR';

        if (!isPlatformBrowser(this.platformId)) {
            this.transloco.setActiveLang(normalized);
            return;
        }

        localStorage.setItem(STORAGE_KEY, normalized);

        const requestId = ++this.langSwitchRequest;
        preloadAppLang(this.transloco, normalized).subscribe(() => {
            if (requestId !== this.langSwitchRequest) {
                return;
            }

            this.transloco.setActiveLang(normalized);
        });
    }

    getActive(): string {
        return this.transloco.getActiveLang();
    }
}
